// Every slot this environment offers, with the projects that have it and a switch-on for
// the rest (docs/Y2026-Q4/design-agent-configs.md section 11). Each project's own
// Settings tab manages the slots it has.
import { useState } from "react";

import {
  type AgentConfig,
  type SlotStart,
  useAgentConfigs,
  useEnableConfigOnProjects,
} from "../api/hooks/useAgentConfigs";
import { ProjectEnablementList } from "../components/ProjectEnablementList";
import { SettingSubcard } from "../components/SettingSubcard";
import { SettingsGroup } from "../components/SettingsGroup";
import { SECTION_TITLES } from "../components/settingsSections";
import { slotKey, slotName, slotStatusText } from "../utils/agentSlotText";
import { planNote } from "../utils/platforms";
import { plural } from "./projectFilesCounts";
import styles from "./TeamSettingsTab.module.css";

// /agent-configs/enable-many refuses more than this in one call.
const MAX_PROJECTS = 5;

interface ProjectOption {
  project_id: string;
  project_name: string;
}

export const TeamAgentConfigsCard = ({ projects }: { projects: ProjectOption[] }) => {
  const { data, error } = useAgentConfigs();
  const slots = (data?.configs ?? []).flatMap((config) =>
    (config.starts ?? []).map((start) => ({ config, start })),
  );
  if (!error && slots.length === 0) return null;
  return (
    <SettingsGroup title={SECTION_TITLES.configs}>
      {error ? (
        <p className={styles.error}>Could not load the agent configurations.</p>
      ) : (
        slots.map(({ config, start }) => (
          <SlotCard
            key={slotKey(config.key, start)}
            config={config}
            start={start}
            projects={projects}
          />
        ))
      )}
    </SettingsGroup>
  );
};

const SlotCard = ({
  config,
  start,
  projects,
}: {
  config: AgentConfig;
  start: SlotStart;
  projects: ProjectOption[];
}) => {
  const enable = useEnableConfigOnProjects();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const slot = slotKey(config.key, start);
  const withSlot = (config.projects ?? []).filter((p) => p.slot === slot);
  const hasSlot = new Set(withSlot.map((p) => p.project_id));
  const offered = projects.filter((p) => !hasSlot.has(p.project_id));
  const paused = withSlot.filter((p) => p.status === "paused").length;
  const names = new Map(projects.map((p) => [p.project_id, p.project_name]));
  const toggle = (projectId: string, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(projectId);
      else next.delete(projectId);
      return next;
    });

  return (
    <SettingSubcard
      title={slotName({ label: config.label, start })}
      describe={config.description}
      notes={[config.cost_note, planNote(config.platform_plan)].filter(
        (note): note is string => !!note,
      )}
    >
      <ProjectEnablementList
        rows={[
          ...withSlot.map((p) => ({
            project_id: p.project_id,
            project_name: p.project_name,
            on: true,
            detail: (
              <span className={p.status === "active" ? styles.muted : styles.paused}>
                {slotStatusText(p.status, p.paused_reason)}
              </span>
            ),
          })),
          ...offered.map((p) => ({ ...p, on: false })),
        ]}
        saving={{ mode: "batched", selected, max: MAX_PROJECTS, busy: enable.isLoading }}
        enabledNote={paused > 0 ? `(${paused} paused)` : ""}
        offHeading="Available"
        allOn="No other projects with scheduled runs on."
        onChange={toggle}
      >
        {offered.length > 0 && (
          <div className={styles.controls}>
            {selected.size >= MAX_PROJECTS && (
              <span className={styles.muted}>At most {MAX_PROJECTS} at a time.</span>
            )}
            <button
              type="button"
              disabled={selected.size === 0 || enable.isLoading}
              onClick={() =>
                enable.mutate(
                  { config_key: config.key, start, project_ids: [...selected] },
                  { onSuccess: () => setSelected(new Set()) },
                )
              }
            >
              {enable.isLoading
                ? "Switching on…"
                : selected.size === 0
                  ? "Switch on"
                  : `Switch on for ${plural(selected.size, "project")}`}
            </button>
          </div>
        )}
      </ProjectEnablementList>
      {enable.isError && (
        <p className={styles.error}>Could not switch it on: {enable.error.message}</p>
      )}
      {enable.data && (
        <ul className={styles.results}>
          {enable.data.results.map((r) => (
            <li key={r.project_id} className={r.error ? styles.error : styles.muted}>
              {names.get(r.project_id) ?? r.project_id}: {r.error ?? "switched on"}
            </li>
          ))}
        </ul>
      )}
    </SettingSubcard>
  );
};
